import jsPDF from "jspdf";
import "jspdf-autotable";
import { format } from "date-fns";
import { drawReportHeader, stampPageNumbers } from "../utils/reports/pdfChrome";
import { downloadBlob } from "../utils/reports/downloadBlob";
import { buildReportFilename } from "../utils/reports/reportFilename";

const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL("image/jpeg");
      resolve({ dataURL, width: img.width, height: img.height });
    };
    img.onerror = (err) => {
      reject(err);
    };
    img.src = url;
  });
};

const calculateImageDimensions = (imgWidth, imgHeight, maxWidth, maxHeight) => {
  let width = imgWidth;
  let height = imgHeight;

  if (width > maxWidth) {
    const scaleFactor = maxWidth / width;
    width *= scaleFactor;
    height *= scaleFactor;
  }

  if (height > maxHeight) {
    const scaleFactor = maxHeight / height;
    width *= scaleFactor;
    height *= scaleFactor;
  }

  return { width, height };
};

const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const generateSingleIssuePDF = async (issue, propertyId) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  const margin = 14;
  const maxImageWidth = (pageWidth - margin * 2 - 20) / 3;
  const maxImageHeight = 50;

  // Standardized header
  let yOffset = drawReportHeader(pdf, {
    title: "Issue Report",
    dateRange: `Issue #${issue.id.slice(0, 6)}`,
  });

  // Priority and Status row
  pdf.setFontSize(11);
  pdf.setFont(undefined, "bold");
  pdf.text("Priority:", margin, yOffset);
  pdf.setFont(undefined, "normal");
  const priorityLabel = PRIORITY_LABELS[issue.priority] || "Medium";
  pdf.text(priorityLabel, margin + 25, yOffset);

  pdf.setFont(undefined, "bold");
  pdf.text("Status:", margin + 60, yOffset);
  pdf.setFont(undefined, "normal");
  pdf.text(issue.status || "Not Reported", margin + 80, yOffset);

  yOffset += 8;

  // Assigned To and Vendor
  if (issue.assignedTo) {
    pdf.setFont(undefined, "bold");
    pdf.text("Assigned To:", margin, yOffset);
    pdf.setFont(undefined, "normal");
    pdf.text(issue.assignedTo, margin + 35, yOffset);
  }

  if (issue.vendor && issue.vendor !== "unknown") {
    pdf.setFont(undefined, "bold");
    pdf.text("Vendor:", margin + 80, yOffset);
    pdf.setFont(undefined, "normal");
    pdf.text(issue.vendor, margin + 100, yOffset);
  }

  yOffset += 8;

  // Submitted date
  if (issue.submittedAt) {
    const submittedDate = issue.submittedAt.toDate
      ? format(issue.submittedAt.toDate(), "MMMM do, yyyy h:mm a")
      : "N/A";
    pdf.setFont(undefined, "bold");
    pdf.text("Submitted:", margin, yOffset);
    pdf.setFont(undefined, "normal");
    pdf.text(submittedDate, margin + 30, yOffset);
  }

  yOffset += 15;

  // Description section
  pdf.setFillColor(245, 245, 245);
  pdf.rect(margin, yOffset - 5, pageWidth - margin * 2, 10, "F");
  pdf.setFontSize(12);
  pdf.setFont(undefined, "bold");
  pdf.text("Description", margin + 2, yOffset + 2);
  yOffset += 12;

  pdf.setFontSize(10);
  pdf.setFont(undefined, "normal");
  const descriptionLines = pdf.splitTextToSize(
    issue.issueDetails || "No description provided.",
    pageWidth - margin * 2
  );
  pdf.text(descriptionLines, margin, yOffset);
  yOffset += descriptionLines.length * 5 + 10;

  // Files section
  if (issue.files && issue.files.length > 0) {
    // Check if we need a new page
    if (yOffset > pageHeight - 80) {
      pdf.addPage();
      yOffset = margin;
    }

    pdf.setFillColor(245, 245, 245);
    pdf.rect(margin, yOffset - 5, pageWidth - margin * 2, 10, "F");
    pdf.setFontSize(12);
    pdf.setFont(undefined, "bold");
    pdf.text(`Attachments (${issue.files.length})`, margin + 2, yOffset + 2);
    yOffset += 15;

    // Separate quotes and other files
    const quotes = issue.files.filter((f) => f.isQuote);
    const otherFiles = issue.files.filter((f) => !f.isQuote);

    // Display quotes
    if (quotes.length > 0) {
      pdf.setFontSize(10);
      pdf.setFont(undefined, "bold");
      pdf.text(`Quotes (${quotes.length}):`, margin, yOffset);
      yOffset += 6;
      pdf.setFont(undefined, "normal");

      quotes.forEach((file, index) => {
        pdf.setTextColor(25, 118, 210);
        pdf.textWithLink(
          `• Quote ${index + 1}: ${file.name || "Quote.pdf"}`,
          margin + 5,
          yOffset,
          { url: file.url }
        );
        pdf.setTextColor(0, 0, 0);
        yOffset += 5;
      });
      yOffset += 5;
    }

    // Display images
    const imageFiles = otherFiles.filter((f) => {
      const ext = f.name?.split(".").pop()?.toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    });

    if (imageFiles.length > 0) {
      pdf.setFontSize(10);
      pdf.setFont(undefined, "bold");
      pdf.text(`Images (${imageFiles.length}):`, margin, yOffset);
      yOffset += 8;

      let xOffset = margin;
      let imagesInRow = 0;

      for (const file of imageFiles) {
        // Check if we need a new page
        if (yOffset > pageHeight - maxImageHeight - 20) {
          pdf.addPage();
          yOffset = margin;
          xOffset = margin;
          imagesInRow = 0;
        }

        try {
          const { dataURL, width, height } = await loadImageAsBase64(file.url);
          const { width: scaledWidth, height: scaledHeight } =
            calculateImageDimensions(width, height, maxImageWidth, maxImageHeight);

          pdf.addImage(dataURL, "JPEG", xOffset, yOffset, scaledWidth, scaledHeight);
          pdf.link(xOffset, yOffset, scaledWidth, scaledHeight, { url: file.url });

          imagesInRow++;
          if (imagesInRow >= 3) {
            imagesInRow = 0;
            xOffset = margin;
            yOffset += maxImageHeight + 5;
          } else {
            xOffset += maxImageWidth + 10;
          }
        } catch (error) {
          console.error("Error loading image:", error);
        }
      }

      if (imagesInRow > 0) {
        yOffset += maxImageHeight + 10;
      }
    }

    // Display other files (non-image, non-quote)
    const otherNonImageFiles = otherFiles.filter((f) => {
      const ext = f.name?.split(".").pop()?.toLowerCase();
      return !["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    });

    if (otherNonImageFiles.length > 0) {
      pdf.setFontSize(10);
      pdf.setFont(undefined, "bold");
      pdf.text(`Other Files (${otherNonImageFiles.length}):`, margin, yOffset);
      yOffset += 6;
      pdf.setFont(undefined, "normal");

      otherNonImageFiles.forEach((file) => {
        pdf.setTextColor(25, 118, 210);
        pdf.textWithLink(`• ${file.name || "File"}`, margin + 5, yOffset, {
          url: file.url,
        });
        pdf.setTextColor(0, 0, 0);
        yOffset += 5;
      });
    }
  }

  // Standardized page numbers on every page
  stampPageNumbers(pdf);

  // Save the PDF
  downloadBlob(
    pdf.output("blob"),
    buildReportFilename(`Issue_${issue.id.slice(0, 6)}`, { ext: "pdf" })
  );
};

export default generateSingleIssuePDF;
